import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { find } from 'lodash-es';
import { Profile } from '@dev/ee-api/ee-api-models';
import { getAccountProfileStore } from '@/store/account/actions';
import { getGeneralInfo } from '@/store/account/actions';
import { getValidEmailsStore } from '@/store/validemails/actions';
import { getDomainsStore } from '@/store/domain/actions';
import { isDomainVerified } from '@/services/tools/isDomainVerified';

type DefaultSenderOptionType = {
    email: string;
    name: string;
};

const getNameFromProfile = (profile: Profile): string => {
    if (!!profile.Company) {
        return profile.Company;
    }
    if (!!profile.FirstName && !!profile.LastName) {
        return `${profile.FirstName} ${profile.LastName}`;
    }
    return profile?.FirstName;
};

export const useGetDefaultSender = (): DefaultSenderOptionType | null => {
    const value = useRef<DefaultSenderOptionType | null>();
    const profile = useSelector(getAccountProfileStore);
    const generalInfo = useSelector(getGeneralInfo);
    const { emails, loadedEmails } = useSelector(getValidEmailsStore);
    const { domains, loadedDomains } = useSelector(getDomainsStore);

    useEffect(() => {
        if (value.current) {
            return;
        }
        if (generalInfo?.IsSub) {
            value.current = {
                email: generalInfo?.DefaultSender ?? generalInfo?.UserName ?? '',
                name: getNameFromProfile(profile) ?? '',
            };
            return;
        }
        if (!generalInfo || !profile || !loadedEmails || !loadedDomains) {
            return;
        }
        const name = getNameFromProfile(profile);
        const validProfileEmail = find(emails, (item) => item.Validated && item.Email === profile.Email);

        if (generalInfo.DefaultSender || validProfileEmail) {
            value.current = {
                email: generalInfo.DefaultSender || validProfileEmail?.Email,
                name,
            };
            return;
        }

        const profileEmailDomain = profile.Email.split('@')?.[1];
        const validDomain = find(domains, (item) => isDomainVerified(item) && item.Domain === profileEmailDomain);

        if (validDomain) {
            value.current = {
                email: profile.Email,
                name,
            };
            return;
        }

        value.current = {
            email: '',
            name,
        };
    }, [generalInfo, profile, loadedEmails, loadedDomains]);

    return value.current;
};
